/**
 * 教学确认（TEACH-PAGES-117；方案第 6 节那张表，10 项）：一张清楚的教学说明，主按钮「确认，教 TA 这个动作」，次按钮「再改一改」。
 * - 确认带页面上这一版的 spec_sha256（adm1：对不上回 409）；同一单同一版再点原样返回、不再占次数（幂等键沿用）。
 * - 次数只信服务端：用完时确认键不可按，写「收费即将开放」那一句（确认回 409 teach_quota_exhausted 时也一样）。
 * - 确认没收到回音（网络断、超时、服务端出错）：结果可能已经记下了——照「结果待核」说，去重读这一单；再点沿用同一个键，不会重复扣（评审中 16）。
 * - 「再改一改」：就地改这份说明（PUT spec 出一版新的，确认时带新的 sha）。打开时焦点落在名称框，改完、不改了焦点回到说明表（评审高 6）。
 *   输入框用全站的 .ps-input、.ps-check（评审阻断 2、高 8、高 9）；名称带计数，「改好了」不能点时说为什么（评审中 14）。
 * - 确认前可以不教：先问一句再取消（评审中 10）。
 * - 视觉照 r7k 05-confirm-A（TEACH-PAGES-VISUAL-117，I 10-16 通过）：说明表上面四项是动作本身（名称、具体动作、做完回到、长短，各带一个小图标），
 *   I 10-16 定的收法（设计评审第二轮：确认键原来在第二屏）：不放标题卡（名字就是说明表第一行，「等你确认」是卡上沿一个小牌）；
 *   次数、扣次规则在桌上的托盘卡里、紧挨确认键（扣次之前看得清：「用本账号 1 次赠送机会，当前可用 N 次」，N 照服务端的数，不在前端减）；
 *   教谁、动作气质、何时使用、参考、学会以后收进「更多说明」（原生 details，键盘、读屏都能开）。方案第 6 节一项不少。
 *   TA 在桌边探头，按钮在托盘卡里（木色桌面上全站焦点环只有 2.53:1、次要键边线 2.52:1，卡面上回到 3.36 以上，设计评审第二轮阻断）。
 *   参考照片的缩略图上传口接上（TEACH-WIRE-117）以后放在说明卡首屏，不收进「更多说明」（I 10-16）。
 *   参考稿的「大约几秒」「开始姿势」：说明里没有秒数，不编；姿势只有 end_pose（做完回到哪个姿势），照它写。参考的拍立得要上传口（TEACH-WIRE-117），这一版不放。
 * - 评审第二轮：按钮请求中不真禁用（焦点不丢）；确认成功后焦点落到本次学习那一页的标题；次数用完时说「这份说明先留着」并接到确认键的说明上；
 *   没收到回音时每失败一次就重读一次这一单。
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toApiError } from "@/shared/api/errors";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, Icon, type IconName } from "@/shared/ui";
import {
  CONFIRM_CHARGE, CONFIRM_MORE, CONFIRM_LATER, CONFIRM_QUOTA, CONFIRM_REFS, CONFIRM_UNKNOWN, CONFIRM_USE, DROP, DROP_ASK, DROP_YES, LENGTH_LABEL, POSE_LABEL, TEACH_CONFIRM, TEACH_REVISE, TRIGGER_LABEL,
  canTeachNew, entitlementLine, stateCopy,
} from "./teachModel";
import type { TeachLength, TeachOrder, TeachSpec, TeachTrigger } from "./teachTypes";
import { teachKeys, useEntitlement } from "./useTeach";
import { useTeachAction } from "./teachActions";
import { TeachAsk } from "./TeachAsk";
import { TeachButton } from "./TeachButton";
import { OrderHeader, SpecSummary, type OrderPet } from "./TeachOrderPage";
import { DeskPeek } from "./TeachDescribePage";

export const CONFIRM_MISSING = "这份说明还没整理好，暂时不能确认。";

export function TeachConfirm({ order, pet }: { order: TeachOrder; pet: OrderPet }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const userId = useOptionalCurrentHousehold()?.userId ?? "-";
  const [editing, setEditing] = useState(false);
  const [asking, setAsking] = useState(false);
  const table = useRef<HTMLElement>(null);
  const dropButton = useRef<HTMLButtonElement>(null);
  const returnToTable = useRef(false);
  const entitlement = useEntitlement();
  const spec = order.spec ?? null;
  const closeEditor = () => {
    returnToTable.current = true;
    setEditing(false);
  };
  // 确认成功：这一页换成本次学习，焦点落到那一页的标题上（OrderHeader 认 teachFocus）
  const confirm = useTeachAction<string>(order, "confirm", (api, key, sha) => api.confirm(order.order_id, sha, key), () => navigate(location.pathname, { replace: true, state: { teachFocus: "title", at: Date.now() } }));
  const save = useTeachAction<TeachSpec>(order, "spec", (api, _key, next) => api.editSpec(order.order_id, next), closeEditor);
  const drop = useTeachAction<null>(order, "cancel", (api, key) => api.cancel(order.order_id, key), () => navigate("/me/moves", { replace: true }));
  const quota = entitlement.data ?? null;
  const exhausted = quota ? !canTeachNew(quota) : false;
  const confirmError = confirm.error ? toApiError(confirm.error) : null;
  const quotaRefused = confirmError?.details?.reason === "teach_quota_exhausted";
  const unknown = Boolean(confirmError && (confirmError.kind === "network" || confirmError.kind === "timeout" || (confirmError.status ?? 0) >= 500));
  useEffect(() => {
    if (unknown) void queryClient.invalidateQueries({ queryKey: teachKeys.order(userId, order.order_id) });
    // 每失败一次就重读一次（confirm.error 每次是新的对象）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm.error]);
  useEffect(() => {
    if (!editing && returnToTable.current) {
      returnToTable.current = false;
      // 说明表比一屏还高时，focus 自带的滚动不动（卡顶会留在顶栏后面）：聚焦不滚，再按 scroll-margin 把卡顶滚到顶栏下面（评审第二轮中 1）
      table.current?.focus({ preventScroll: true });
      table.current?.scrollIntoView?.({ block: "start" });
    }
  }, [editing]);
  // 草稿页整理好了跳过来（TEACH-UNDERSTAND-UI-117，location.state.teachFocus＝"spec"）：焦点落在说明表上，和「再改一改」回来时同一个落点
  useEffect(() => {
    if ((location.state as { teachFocus?: string } | null)?.teachFocus !== "spec") return;
    table.current?.focus({ preventScroll: true });
    table.current?.scrollIntoView?.({ block: "start" });
  }, [location.state]);
  const copy = stateCopy(order.state);
  if (!spec || !order.spec_sha256) {
    return (
      <>
        <OrderHeader order={order} pet={pet} />
        <p className="ps-teach-note" role="status">{CONFIRM_MISSING}</p>
      </>
    );
  }
  const errorText = !confirmError ? null : quotaRefused && quota ? entitlementLine({ ...quota, exhausted: true, available: 0 }) : unknown ? CONFIRM_UNKNOWN : confirmError.playerMessage;
  return (
    <>
      {copy ? <p className="ps-teach-note">{copy.line}</p> : null}
      {editing ? (
        <SpecEditor spec={spec} busy={save.isPending} error={save.error} onCancel={closeEditor} onSave={(next) => save.mutate(next)} />
      ) : (
        <section className="ps-teach-card ps-teach-confirm" aria-label="教学说明" ref={table} tabIndex={-1}>
          {copy ? <p className="ps-teach-who__tag ps-teach-confirm__tag" data-state={order.state}>{copy.tag}</p> : null}
          <dl className="ps-teach-spec ps-teach-spec--main">
            <Row term="动作名称" icon="sparkle">{spec.title}</Row>
            <Row term="具体动作" icon="diary"><SpecSummary steps={spec.steps} /></Row>
            {POSE_LABEL[spec.end_pose] ? <Row term="做完回到" icon="pin">{POSE_LABEL[spec.end_pose]}</Row> : null}
            <Row term="长短" icon={LENGTH_ICON[spec.length] ?? "play"}>{LENGTH_LABEL[spec.length] ?? null}</Row>
          </dl>
          <details className="ps-teach-more">
            <summary className="ps-teach-more__summary">{CONFIRM_MORE}</summary>
            <dl className="ps-teach-spec">
              <Row term="教谁">{pet.name}</Row>
              {spec.temperament.trim() ? <Row term="动作气质">{spec.temperament}</Row> : null}
              <Row term="何时使用">{spec.trigger.map((t) => TRIGGER_LABEL[t]).filter(Boolean).join("、")}</Row>
              {spec.refs && spec.refs.length ? <Row term="参考">{CONFIRM_REFS(spec.refs.length)}</Row> : null}
              <Row term="学会以后">{CONFIRM_USE}</Row>
            </dl>
          </details>
        </section>
      )}
      <section className="ps-teach-desk ps-teach-desk--confirm">
        {editing ? null : <DeskPeek pet={pet} />}
        <div className="ps-teach-desk__top">
          <div className="ps-teach-card ps-teach-tray">
          {editing ? null : (
            <>
              {/* 扣次之前就在确认键旁边看得清（I 10-16） */}
              <dl className="ps-teach-spec ps-teach-spec--tray" aria-label="次数">
                <Row term="次数">{quota ? (exhausted ? entitlementLine(quota) : CONFIRM_QUOTA(quota.available)) : "正在看本账号还有几次…"}</Row>
                <Row term="扣次规则">{CONFIRM_CHARGE}</Row>
              </dl>
              {errorText ? <p className="ps-teach-form__error" role="alert">{errorText}</p> : null}
              {exhausted || quotaRefused ? <p className="ps-teach-note" id="teach-confirm-later">{CONFIRM_LATER}</p> : null}
              <div className="ps-teach-actions">
                <TeachButton variant="primary" block busy={confirm.isPending} unavailable={!quota || exhausted || quotaRefused} describedBy={exhausted || quotaRefused ? "teach-confirm-later" : undefined} onClick={() => confirm.mutate(order.spec_sha256!)}>{TEACH_CONFIRM}</TeachButton>
                <TeachButton variant="secondary" block unavailable={confirm.isPending} onClick={() => setEditing(true)}>{TEACH_REVISE}</TeachButton>
              </div>
            </>
          )}
          <div className="ps-teach-quietactions">
            {asking ? (
              <TeachAsk label={DROP} text={DROP_ASK} yes={DROP_YES} busy={drop.isPending} onYes={() => drop.mutate(null)} onNo={() => setAsking(false)} returnTo={dropButton} />
            ) : (
              <TeachButton buttonRef={dropButton} variant="ghost" unavailable={confirm.isPending} onClick={() => setAsking(true)}>{DROP}</TeachButton>
            )}
            {drop.error ? <p className="ps-teach-form__error" role="alert">{toApiError(drop.error).playerMessage}</p> : null}
          </div>
          </div>
        </div>
      </section>
    </>
  );
}

/** 说明表的一行；动作本身那四项前面有一个圆底小图标（只是装饰，读屏只念字） */
function Row({ term, icon, children }: { term: string; icon?: IconName; children: ReactNode }) {
  return (
    <div className={`ps-teach-spec__row${icon ? " has-icon" : ""}`}>
      {icon ? <span className="ps-teach-spec__icon" aria-hidden="true"><Icon name={icon} size={18} /></span> : null}
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

const LENGTHS: TeachLength[] = ["once", "repeat_short", "loop"];
/** 「长短」那一行的小图标跟着取值走：做一次就停是播放一次，连做、一直做才用循环（6c2b 轻审：原来一律循环图标，和「做一次就停」打架） */
const LENGTH_ICON: Record<TeachLength, IconName> = { once: "play", repeat_short: "refresh", loop: "refresh" };
const TRIGGERS: TeachTrigger[] = ["owner_tap", "self"];
const TITLE_MAX = 20;
const STEP_MAX = 40;
const MOOD_MAX = 60;

/** 为什么「改好了」现在不能点（一次只说第一件）。 */
export function specProblem(title: string, steps: readonly string[], trigger: readonly TeachTrigger[]): string | null {
  if (!title.trim()) return "先给这个动作起个名字。";
  if (!steps.some((s) => s.trim())) return "至少写一步。";
  if (!trigger.length) return "至少选一种什么时候用。";
  return null;
}

/**
 * 「再改一改」：名字、步骤（1–4 步）、气质、长短、什么时候用。在哪儿做、做完回到哪种姿势照原来的（取值由 C 定，页面不另造）。
 * 清空一步就是去掉它（表单上写明）。
 */
export function SpecEditor({ spec, busy, error, onSave, onCancel }: { spec: TeachSpec; busy: boolean; error: unknown; onSave: (spec: TeachSpec) => void; onCancel: () => void }) {
  const [title, setTitle] = useState(spec.title);
  const [steps, setSteps] = useState<string[]>(spec.steps.length ? [...spec.steps] : [""]);
  const [temperament, setTemperament] = useState(spec.temperament);
  const [length, setLength] = useState<TeachLength>(spec.length);
  const [trigger, setTrigger] = useState<TeachTrigger[]>(spec.trigger);
  const titleInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    titleInput.current?.focus();
  }, []);
  const clean = steps.map((s) => s.trim()).filter(Boolean);
  const problem = specProblem(title, steps, trigger);
  return (
    <form
      className="ps-teach-card ps-teach-form"
      aria-label="改一改教学说明"
      onSubmit={(event) => {
        event.preventDefault();
        if (!problem && !busy) onSave({ ...spec, title: title.trim(), steps: clean.slice(0, 4), temperament: temperament.trim(), length, trigger });
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && !busy) {
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <label className="ps-teach-form__label" htmlFor="teach-title">动作名称</label>
      <input id="teach-title" ref={titleInput} className="ps-input" value={title} maxLength={TITLE_MAX} aria-describedby="teach-title-count" onChange={(e) => setTitle(e.target.value)} />
      <p className="ps-teach-form__count" id="teach-title-count">{title.length}/{TITLE_MAX}</p>
      <fieldset className="ps-teach-form__set">
        <legend className="ps-teach-form__label">具体动作（1 到 4 步）</legend>
        <p className="ps-teach-note ps-teach-form__hint">每一步写 TA 做的一个动作；清空一步就是去掉它。</p>
        {steps.map((step, index) => (
          <input
            key={index}
            className="ps-input"
            aria-label={`第 ${index + 1} 步`}
            value={step}
            maxLength={STEP_MAX}
            onChange={(e) => setSteps(steps.map((s, i) => (i === index ? e.target.value : s)))}
          />
        ))}
        {steps.length < 4 ? <Button variant="ghost" size="sm" className="ps-teach-form__more" onClick={() => setSteps([...steps, ""])}>再加一步</Button> : null}
      </fieldset>
      <label className="ps-teach-form__label" htmlFor="teach-mood">动作气质</label>
      <input id="teach-mood" className="ps-input" value={temperament} maxLength={MOOD_MAX} onChange={(e) => setTemperament(e.target.value)} />
      <fieldset className="ps-teach-form__set">
        <legend className="ps-teach-form__label">长短</legend>
        {LENGTHS.map((value) => (
          <label key={value} className="ps-check">
            <input type="radio" name="teach-length" checked={length === value} onChange={() => setLength(value)} />
            {LENGTH_LABEL[value]}
          </label>
        ))}
      </fieldset>
      <fieldset className="ps-teach-form__set">
        <legend className="ps-teach-form__label">何时使用</legend>
        {TRIGGERS.map((value) => (
          <label key={value} className="ps-check">
            <input type="checkbox" checked={trigger.includes(value)} onChange={(e) => setTrigger(e.target.checked ? [...trigger, value] : trigger.filter((t) => t !== value))} />
            {TRIGGER_LABEL[value]}
          </label>
        ))}
      </fieldset>
      {problem ? <p className="ps-teach-note" role="status" id="teach-spec-problem">{problem}</p> : null}
      {error ? <p className="ps-teach-form__error" role="alert">{toApiError(error).playerMessage}</p> : null}
      <div className="ps-teach-actions">
        <TeachButton type="submit" variant="primary" block busy={busy} unavailable={Boolean(problem)} describedBy={problem ? "teach-spec-problem" : undefined}>改好了</TeachButton>
        <TeachButton variant="secondary" block unavailable={busy} onClick={onCancel}>不改了</TeachButton>
      </div>
    </form>
  );
}
